import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import extractText from 'flarum/common/utils/extractText';
import uploadFile from '../utils/uploadFile';
import FileButton from './FileButton';
/**
 * Minimal contenteditable-based rich text editor for a cartothèque entry's
 * public description (see CartothequeEntryEditModal) - deliberately not the
 * forum's own BBCode composer: this content is never a forum post, and a
 * full posting composer would drag in reply-composing concerns (drafts,
 * mentions, previews) that have nothing to do with this. The toolbar covers
 * exactly what CartothequeEntryResource::purifyDescription() allows through
 * server-side - bold/italic/underline, lists, images (uploaded via the same
 * fof/upload endpoint as finding photos/card logos) - nothing more, since
 * anything this editor can't produce would just get silently stripped on
 * save anyway.
 *
 * Uncontrolled by design: the contenteditable div's own innerHTML is the
 * single source of truth for what's typed. attrs.value only seeds the
 * initial content once, in oncreate() - a "controlled" contenteditable that
 * resets innerHTML from a vnode attr on every redraw fights the browser's
 * own text selection/cursor position, easy to end up with a cursor that
 * jumps to the start on every keystroke. Every change is read directly off
 * the DOM node on demand via getValue(), called by the parent's own
 * submit() rather than pushed up on every keystroke.
 */
export default class CartothequeDescriptionEditor extends Component {
  uploading = false;
  editorEl = null;
  oninit(vnode) {
    super.oninit(vnode);
    this.uploading = false;
  }
  oncreate(vnode) {
    super.oncreate(vnode);
    this.editorEl = this.element.querySelector('.ChampisCartothequeDescriptionEditor-content');
    this.editorEl.innerHTML = this.attrs.value || '';

    // Lets the parent read getValue() back at its own submit() time,
    // without this component pushing every keystroke up as a redraw-
    // triggering attr change (see class docblock's "uncontrolled by
    // design" reasoning).
    if (this.attrs.onReady) this.attrs.onReady(this);
  }
  view() {
    const t = key => app.translator.trans(`champis-exhibitions.forum.description_editor.${key}`);
    return m("div", {
      className: "ChampisCartothequeDescriptionEditor"
    }, m("div", {
      className: "ChampisCartothequeDescriptionEditor-toolbar"
    }, m("button", {
      type: "button",
      className: "Button Button--icon",
      title: t('bold'),
      onclick: () => this.exec('bold')
    }, m("i", {
      className: "icon fas fa-bold",
      "aria-hidden": "true"
    })), m("button", {
      type: "button",
      className: "Button Button--icon",
      title: t('italic'),
      onclick: () => this.exec('italic')
    }, m("i", {
      className: "icon fas fa-italic",
      "aria-hidden": "true"
    })), m("button", {
      type: "button",
      className: "Button Button--icon",
      title: t('underline'),
      onclick: () => this.exec('underline')
    }, m("i", {
      className: "icon fas fa-underline",
      "aria-hidden": "true"
    })), m("button", {
      type: "button",
      className: "Button Button--icon",
      title: t('bullet_list'),
      onclick: () => this.exec('insertUnorderedList')
    }, m("i", {
      className: "icon fas fa-list-ul",
      "aria-hidden": "true"
    })), m("button", {
      type: "button",
      className: "Button Button--icon",
      title: t('numbered_list'),
      onclick: () => this.exec('insertOrderedList')
    }, m("i", {
      className: "icon fas fa-list-ol",
      "aria-hidden": "true"
    })), m(FileButton, {
      className: "Button Button--icon",
      icon: "fas fa-image",
      accept: "image/*",
      title: extractText(t('insert_image')),
      disabled: this.uploading,
      onfile: file => this.insertImage(file)
    }), this.uploading && m("span", {
      className: "ChampisCartothequeDescriptionEditor-uploading"
    }, t('uploading'))), m("div", {
      className: "ChampisCartothequeDescriptionEditor-content",
      contenteditable: "true"
    }));
  }
  exec(command, value) {
    if (value === void 0) {
      value = null;
    }
    this.editorEl.focus();
    document.execCommand(command, false, value ?? undefined);
  }
  insertImage(file) {
    if (!file) return;
    this.uploading = true;
    m.redraw();
    uploadFile(file).then(uploaded => {
      this.uploading = false;
      if (uploaded) {
        this.exec('insertImage', uploaded.attributes.url);
      }
      m.redraw();
    }).catch(() => {
      this.uploading = false;
      m.redraw();
    });
  }

  // getValue()'s only caller (CartothequeEntryEditModal's own submit())
  // reads this straight into the JSON:API `description` attribute -
  // server-side purifyDescription() is the real security boundary, this
  // is just where the raw HTML comes from.
  getValue() {
    return this.editorEl.innerHTML;
  }
}
flarum.reg.add('champis-exhibitions', 'forum/components/CartothequeDescriptionEditor', CartothequeDescriptionEditor);